import React from 'react';

import { NotFoundError } from '@eluve/eluve-errors';

import { EntityNotFoundPage } from './EntityNotFoundPage';

export interface NotFoundBoundaryProps {
  entityType: string;
  listPathTemplate?: string;
  listLabel?: string;
  children: React.ReactNode;
  /**
   * When any value in this array changes between renders, the captured error
   * is cleared and children are re-rendered. Required at routes where the
   * same boundary instance is reused across navigations that change a URL
   * param (e.g. `/patients/:patientId`) — without it, the boundary stays
   * stuck on the Not Found UI when the user navigates from a missing entity
   * to a valid one.
   */
  resetKeys?: ReadonlyArray<unknown>;
}

interface State {
  error: unknown;
}

const resetKeysChanged = (
  prev: ReadonlyArray<unknown> | undefined,
  next: ReadonlyArray<unknown> | undefined,
): boolean => {
  if (prev === next) return false;
  if (prev === undefined || next === undefined) return true;
  if (prev.length !== next.length) return true;
  return prev.some((value, index) => !Object.is(value, next[index]));
};

export class NotFoundBoundary extends React.Component<
  NotFoundBoundaryProps,
  State
> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  override componentDidUpdate(prevProps: NotFoundBoundaryProps) {
    if (
      this.state.error !== null &&
      resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.setState({ error: null });
    }
  }

  override render() {
    const { error } = this.state;
    if (error) {
      if (error instanceof NotFoundError) {
        return (
          <EntityNotFoundPage
            entityType={this.props.entityType}
            listPathTemplate={this.props.listPathTemplate}
            listLabel={this.props.listLabel}
          />
        );
      }
      throw error;
    }
    return this.props.children;
  }
}
